/**
 * PWA install hook + helpers.
 *
 * Chrome / Edge / Samsung-Internet fire ``beforeinstallprompt`` when a
 * PWA passes their installability heuristics (manifest + SW + HTTPS +
 * not-already-installed). We capture the event and let UI surfaces
 * trigger the install dialog later — typically a banner on first run
 * + a permanent entry on the settings page.
 *
 * iOS Safari has no programmatic install — the only path is
 * Share → Add to Home Screen. We expose ``platform === 'ios'`` so
 * the UI can show the right instructions in that case.
 *
 * ``isStandalone`` is true once the user has installed the PWA and
 * is launching from the home-screen icon; we hide install prompts
 * in that case (no point asking someone to install again).
 */
import { useCallback, useEffect, useState } from 'react';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<void>;
}

type InstallPlatform = 'chromium' | 'ios' | 'none';

function detectInstallPlatform(): InstallPlatform {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return 'none';
  const ua = navigator.userAgent;
  // iOS Safari + iPadOS — the regex matches the standard iOS UA + iPadOS
  // 13+ which reports as Macintosh + maxTouchPoints > 1.
  const isIos =
    /iPad|iPhone|iPod/.test(ua) ||
    (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  if (isIos) return 'ios';
  // Anything else with beforeinstallprompt support is treated as
  // chromium-flavoured (Chrome / Edge / Samsung / Brave / Opera).
  return 'chromium';
}

function readStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
  // iOS exposes a non-standard navigator.standalone for installed PWAs.
  const navAny = navigator as Navigator & { standalone?: boolean };
  if (navAny.standalone === true) return true;
  return false;
}

export interface PwaInstallState {
  /** "chromium" → install() is programmatic via deferred prompt.
   *  "ios" → user must use Share → Add to Home Screen manually.
   *  "none" → desktop, or browser that doesn't support installs. */
  platform: InstallPlatform;
  /** True once the user is launching from the installed PWA. */
  isStandalone: boolean;
  /** True when install() can be called and will actually prompt the
   *  user. False if no deferred prompt was captured (the browser
   *  decided to suppress it, or the user already installed). */
  canInstall: boolean;
  /** Fires the native install dialog on Chromium browsers. Returns
   *  the outcome promise so callers can react. No-op on other
   *  platforms. */
  install: () => Promise<'accepted' | 'dismissed' | 'unavailable'>;
}

/**
 * Hook for any UI surface that wants to offer "Install app".
 *
 * - Listens for ``beforeinstallprompt`` and stashes the event.
 * - Listens for ``appinstalled`` so the standalone state flips
 *   without a page reload.
 * - Cleans up on unmount.
 */
export function usePwaInstall(): PwaInstallState {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [platform] = useState<InstallPlatform>(() => detectInstallPlatform());
  const [isStandalone, setIsStandalone] = useState<boolean>(() => readStandalone());

  useEffect(() => {
    function onBeforeInstall(e: Event) {
      // Prevent Chrome's auto-mini-infobar so we can prompt later
      // on our own surface (banner / settings card).
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    }
    function onInstalled() {
      setDeferred(null);
      setIsStandalone(true);
      // Persist dismissal so the banner stays hidden if the user comes
      // back to the site in a regular browser tab (not standalone).
      // Without this, opening linzametrics.com in Chrome AFTER installing
      // would still show "Установить как приложение" prompts.
      dismissPwaInstall();
    }
    window.addEventListener('beforeinstallprompt', onBeforeInstall as EventListener);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall as EventListener);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const install = useCallback(async () => {
    if (!deferred) return 'unavailable' as const;
    await deferred.prompt();
    const choice = await deferred.userChoice;
    // The event is single-use per the spec — drop it after prompting
    // so subsequent attempts don't re-fire a dead prompt.
    setDeferred(null);
    return choice.outcome;
  }, [deferred]);

  return {
    platform,
    isStandalone,
    canInstall: !isStandalone && (platform === 'ios' || deferred !== null),
    install,
  };
}

/** Estimated install size (cached precache + the SW itself). Static
 *  enough that we don't bother measuring at runtime — the precache
 *  manifest is fixed per build. Surfaces use it in copy like "~2 MB". */
export const PWA_INSTALL_SIZE_LABEL = '~2 MB';

export const PWA_DISMISS_KEY = 'pwa-install-dismissed';

export function isPwaInstallDismissed(): boolean {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem(PWA_DISMISS_KEY) === '1';
}

export function resetPwaInstallDismissed(): void {
  try {
    window.localStorage.removeItem(PWA_DISMISS_KEY);
  } catch {
    // ignore
  }
}

export function dismissPwaInstall(): void {
  try {
    window.localStorage.setItem(PWA_DISMISS_KEY, '1');
  } catch {
    // Storage disabled — nothing else we can do; banner will reappear
    // on next load but that's the user's choice via Private Browsing.
  }
}
